import { TableClient } from "@azure/data-tables";
import type { TableEntity } from "@azure/data-tables";
import type { TokenCredential } from "@azure/core-auth";
import { makeRowKey } from "@m8t-stack/agent-ledger";
import type { LedgerRow } from "@m8t-stack/agent-ledger/read";
import {
  DEFAULT_SAFETY_LAG_SECONDS,
  ENGINE_VERSION,
  canonicalWorker,
  type AdvancePlan,
  type Cursor,
  type DreamCursor,
  type PkWatermark,
} from "./types.js";

/** The single BrainDreamCursor table (DESIGN §3). PK is fixed; RowKey = canonical worker. */
export const CURSOR_PARTITION_KEY = "dreamcursor";

/**
 * A point-lookup 404 — either the TABLE is absent (first run anywhere:
 * `TableNotFound`) or the ROW is absent (first run for this worker:
 * `ResourceNotFound`). Both mean "no cursor yet"; neither is an error.
 * Mirrors @azure/data-tables RestError shape (statusCode + body errorCode),
 * matching apps/web/lib/gateway/{conversations,init-tables}.ts.
 */
function isAbsentCursorError(e: unknown): boolean {
  const anyE = e as { statusCode?: number; code?: string; details?: { errorCode?: string } };
  return (
    anyE.statusCode === 404 ||
    anyE.code === "ResourceNotFound" ||
    anyE.code === "TableNotFound" ||
    anyE.details?.errorCode === "TableNotFound" ||
    anyE.details?.errorCode === "ResourceNotFound"
  );
}

interface CursorRow {
  partitionKey: string;
  rowKey: string;
  watermarks: string; // JSON Record<physicalPk, PkWatermark>
  safetyLagSeconds?: number;
  engineVersion?: string;
  lastDreamAt?: string;
}

/** The from-the-beginning cursor: empty watermark map, default lag, current engine. */
function absentCursor(canonical: string): DreamCursor {
  return {
    worker: canonical,
    watermarks: {},
    safetyLagSeconds: DEFAULT_SAFETY_LAG_SECONDS,
    engineVersion: ENGINE_VERSION,
  };
}

/**
 * Cursor impl over the BrainDreamCursor Table Storage table (DESIGN §3).
 * The TableClient is INJECTED (unit tests pass a fake; the entry point in §8
 * constructs the real one) — this class reads no env and no wall clock.
 */
export class TableCursor implements Cursor {
  constructor(private readonly client: TableClient) {}

  /**
   * Point-read the canonical worker's cursor row. Catches BOTH the
   * missing-table (TableNotFound) and missing-row (ResourceNotFound) 404s and
   * returns an absent (from-the-beginning) cursor WITHOUT creating the table or
   * writing anything (DESIGN §3 — "createTable is bound only to advance()").
   * Any other error (e.g. 403 RBAC) propagates raw.
   */
  async read(worker: string): Promise<DreamCursor> {
    const canonical = canonicalWorker(worker);
    let row: CursorRow;
    try {
      row = await this.client.getEntity<CursorRow>(CURSOR_PARTITION_KEY, canonical);
    } catch (e) {
      if (isAbsentCursorError(e)) return absentCursor(canonical);
      throw e;
    }
    const watermarks = row.watermarks
      ? (JSON.parse(row.watermarks) as Record<string, PkWatermark>)
      : {};
    return {
      worker: canonical,
      watermarks,
      safetyLagSeconds: row.safetyLagSeconds ?? DEFAULT_SAFETY_LAG_SECONDS,
      engineVersion: row.engineVersion ?? ENGINE_VERSION,
      ...(row.lastDreamAt ? { lastDreamAt: row.lastDreamAt } : {}),
    };
  }

  /**
   * Persist the cursor for the canonical worker. This is the ONLY place that
   * creates the BrainDreamCursor table (DESIGN §3 — read() never does DDL); a
   * TableAlreadyExists race is benign. The non-dry F3 path calls this; F2 ships
   * it wired + unit-tested. The per-PK watermark map is serialized to JSON
   * (Table cells are scalar — same rationale as agent-ledger's `artifacts`).
   */
  async advance(worker: string, cursor: DreamCursor): Promise<void> {
    const canonical = canonicalWorker(worker);
    await this.ensureTable();
    const entity: TableEntity = {
      partitionKey: CURSOR_PARTITION_KEY,
      rowKey: canonical,
      watermarks: JSON.stringify(cursor.watermarks),
      safetyLagSeconds: cursor.safetyLagSeconds,
      engineVersion: cursor.engineVersion,
    };
    if (cursor.lastDreamAt) entity.lastDreamAt = cursor.lastDreamAt;
    await this.client.upsertEntity(entity);
  }

  private async ensureTable(): Promise<void> {
    try {
      await this.client.createTable();
    } catch (e) {
      if (isTableAlreadyExistsError(e)) return;
      throw e;
    }
  }
}

/** A benign create-race: the table was created concurrently (e.g. two workers'
 *  advance() in one nightly run). Mirrors apps/web/lib/gateway/init-tables.ts. */
function isTableAlreadyExistsError(e: unknown): boolean {
  const anyE = e as { statusCode?: number; code?: string; details?: { errorCode?: string } };
  return (
    anyE.statusCode === 409 &&
    (anyE.code === "TableAlreadyExists" || anyE.details?.errorCode === "TableAlreadyExists")
  );
}

/**
 * The minimal row shape the watermark math reads: the emit-time plus the storage
 * RowKey (the dedup key). `LedgerRow` satisfies this, and so does the per-conv
 * harvest provenance (`NormalizedConversation.provenance.rows`) the corpus cap
 * feeds back through `computeWatermark` — so the partial-advance watermark is
 * derived by the SAME function as the full-corpus one (no reverse-engineering).
 */
export type WatermarkRow = Pick<LedgerRow, "eventTimestamp"> & { rowKey?: string; eventId?: string };

/** The storage RowKey of a ledger row — the unique, lexically-ordered key the
 *  cursor dedups on (DESIGN §3: "RowKey is the only sound key"; no eventId
 *  tiebreak). agent-ledger's entity writes `rowKey`; the read maps it through.
 *  `makeRowKey(eventTimestamp, eventId)` is the deterministic fallback for a
 *  row that didn't surface its storage key. Exported so the harvest can stamp
 *  the SAME key onto a conversation's provenance (pipeline.ts) that the cursor
 *  later dedups against. */
export function rowStorageKey(r: WatermarkRow): string {
  return r.rowKey ?? makeRowKey(r.eventTimestamp, r.eventId ?? "");
}

/**
 * The ISO ts exactly 1ms before a fetch-errored group — the latest watermark
 * that is still STRICTLY below it, so the group (and the overlap window before
 * it) is re-scanned next run (DESIGN §2.4). Degrades to the raw ts if it can't
 * be parsed. Shared by buildAdvancePlan (pipeline.ts) and the corpus cap so both
 * apply the IDENTICAL fence.
 */
export function justBefore(failedTs: string): string {
  const t = Date.parse(failedTs);
  return Number.isFinite(t) ? new Date(t - 1).toISOString() : failedTs;
}

const lexMax = (a: string, b: string): string => (a >= b ? a : b);
const lexMin = (a: string, b: string): string => (a <= b ? a : b);

/**
 * Pure watermark math (DESIGN §3) — `now` is INJECTED, never read from the wall
 * clock here (the unit-tested core). Given the rows consumed for one physical
 * PK this run:
 *   ts = min( max(consumed eventTimestamp), now − safetyLag )
 * The clamp to `now − safetyLag` is the silent-drop fix: eventTimestamp is the
 * worker's emit-time, not commit-order, so we never advance past a point that a
 * still-in-flight turn could back-date into. consumedRowKeys = the RowKeys at or
 * after the overlap window start `(ts − safetyLag)` — so a late row that commits
 * with an earlier emit-time inside the lag is re-scanned + deduped next run and
 * never dropped. ISO-8601 UTC sorts lexically (same property the ledger filter
 * relies on), so string compare == time compare.
 */
export function computeWatermark(
  rows: WatermarkRow[],
  now: string,
  safetyLagSeconds: number,
): PkWatermark {
  const lagMs = safetyLagSeconds * 1000;
  const nowMinusLag = new Date(Date.parse(now) - lagMs).toISOString();
  const maxEventTs = rows.reduce<string>((acc, r) => lexMax(acc, r.eventTimestamp), "");
  // No rows → idle this PK at the (now − lag) floor with nothing consumed.
  const ts = maxEventTs ? lexMin(maxEventTs, nowMinusLag) : nowMinusLag;
  const windowStart = new Date(Date.parse(ts) - lagMs).toISOString();
  const consumedRowKeys = rows
    .filter((r) => r.eventTimestamp >= windowStart)
    .map(rowStorageKey);
  return { ts, consumedRowKeys };
}

/**
 * Commit a write-gated AdvancePlan (DESIGN §2.2; folds CRITICAL F1). Called by
 * runDream ONLY after a successful brain write, so a failed write never burns the
 * window. Reuses Cursor.advance (the single DDL site — lazy ensureTable on first
 * use). The plan already carries the fence-corrected per-PK watermarks + this
 * run's consumedRowKeys; this just persists them and stamps lastDreamAt.
 */
export async function commitCursorAdvance(plan: AdvancePlan, cursor: Cursor): Promise<void> {
  const prior = await cursor.read(plan.worker);
  const next: DreamCursor = {
    worker: plan.worker,
    watermarks: plan.watermarks,
    safetyLagSeconds: prior.safetyLagSeconds || DEFAULT_SAFETY_LAG_SECONDS,
    engineVersion: ENGINE_VERSION,
    lastDreamAt: new Date().toISOString(),
  };
  await cursor.advance(plan.worker, next);
}

/**
 * Zero-LLM pre-check (DESIGN §2.1 / §3 #5; folds defer #8). Skip (true) ONLY when
 * EVERY physical PK present in `maxEventTsByPk` has its newest eventTimestamp
 * at/below ITS OWN watermark. A PK present in the rows but ABSENT from the cursor
 * watermarks (a brand-new physical PK — e.g. new a2a traffic, §10) is never
 * at/below a watermark, so the worker is NOT skipped (the global-max bug). An
 * empty watermark map (never-seen worker) is never a skip.
 */
export function skipIfNoNew(maxEventTsByPk: Record<string, string>, cursor: DreamCursor): boolean {
  if (Object.keys(cursor.watermarks).length === 0) return false;
  const pks = Object.keys(maxEventTsByPk);
  if (pks.length === 0) return true; // no ledger rows at all → nothing new
  return pks.every((pk) => {
    if (!Object.hasOwn(cursor.watermarks, pk)) return false; // new PK with rows → not a skip
    return (maxEventTsByPk[pk] ?? "") <= cursor.watermarks[pk].ts;
  });
}

/**
 * The newest eventTimestamp per physical PK — the cheap bounded ledger read that
 * feeds skipIfNoNew (DESIGN §2.1). Pure; ISO-8601 UTC sorts lexically. Keyed on
 * the raw physical PK (agentName), matching the cursor's per-PK watermark keys.
 */
export function computeMaxEventTsByPk(rows: LedgerRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of rows) {
    const pk = r.agentName;
    if (!Object.hasOwn(out, pk) || r.eventTimestamp > out[pk]) out[pk] = r.eventTimestamp;
  }
  return out;
}

/** The BrainDreamCursor Table Storage table name (DESIGN §3). */
export const CURSOR_TABLE_NAME = "BrainDreamCursor";

/**
 * Edge constructor for the live `Cursor`: builds a `TableClient` for the
 * BrainDreamCursor table from a credential + the storage account's table
 * endpoint, then wraps it in `TableCursor` (which reads no env / wall clock).
 * The dry path only calls `.read()`, which tolerates a missing table (DESIGN
 * §3 — only `.advance()` does DDL). Unit tests inject fakes and never reach
 * this; the CLI's `defaultDeps` calls it on the live path.
 */
export function makeTableCursor(credential: TokenCredential, tableEndpoint: string): Cursor {
  const client = new TableClient(tableEndpoint, CURSOR_TABLE_NAME, credential);
  return new TableCursor(client);
}

/** Human-readable, secret-free dry-run line: the watermark + lag advance()
 *  WOULD write per physical PK (DESIGN §3 — "dry-run writes nothing, prints
 *  what it would advance to"). */
export function formatCursorPreview(cursor: DreamCursor): string {
  const head = `cursor: worker=${cursor.worker} safetyLag=${String(cursor.safetyLagSeconds)}s engine=${cursor.engineVersion}`;
  const pks = Object.entries(cursor.watermarks);
  if (pks.length === 0) return `${head}\n  (from the beginning — no prior watermark)`;
  const lines = pks.map(
    ([pk, w]) => `  ${pk} → ts=${w.ts} (${String(w.consumedRowKeys.length)} consumed)`,
  );
  return [head, ...lines].join("\n");
}
